import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthStore } from "../stores/authStore";
import { useDomainStore } from "../stores/domainStore";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "./ui/DropdownMenu";
import { Button } from "./ui/Button";
import { Avatar, AvatarFallback } from "./ui/Avatar";
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/Dialog";
import { Input } from "./ui/Input";

function ChevronDown({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function ChevronRight({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function FolderIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  );
}

function DocumentIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function ChatIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function UsersIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function ShieldIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function PlusIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function NetworkIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

export default function Sidebar() {
  const { pathname } = useLocation();
  const { user, logout } = useAuthStore();
  const { domains, isLoading, fetchDomains, createDomain } = useDomainStore();
  const [expandedDomains, setExpandedDomains] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newDomainName, setNewDomainName] = useState("");
  const [newDomainSlug, setNewDomainSlug] = useState("");
  const [newDomainDesc, setNewDomainDesc] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (user) fetchDomains();
  }, [user, fetchDomains]);

  // Auto-expand the current domain
  useEffect(() => {
    const match = pathname.match(/\/domains\/([^/]+)/);
    if (match) {
      setExpandedDomains((prev) => new Set(prev).add(match[1]));
    }
  }, [pathname]);

  function toggleDomain(domainId) {
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domainId)) next.delete(domainId);
      else next.add(domainId);
      return next;
    });
  }

  async function handleCreateDomain(e) {
    e.preventDefault();
    setCreating(true);
    try {
      await createDomain({
        name: newDomainName,
        slug: newDomainSlug || undefined,
        description: newDomainDesc || undefined,
      });
      setDialogOpen(false);
      setNewDomainName("");
      setNewDomainSlug("");
      setNewDomainDesc("");
    } catch {
      // ignore, store handles errors
    } finally {
      setCreating(false);
    }
  }

  // Updated logout handler for Keycloak
  const handleLogout = async () => {
    await logout();
  };

  const isActive = (path) => pathname === path || pathname.startsWith(path + "/");

  if (!user) return null;

  return (
    <>
      <aside className="fixed left-0 top-0 z-30 h-screen w-64 bg-slate-900 text-slate-300 flex flex-col border-r border-slate-800">
        {/* Logo */}
        <div className="flex h-14 items-center px-4 border-b border-slate-800">
          <Link to="/domains" className="text-lg font-bold text-white">
            Digitize RAG
          </Link>
        </div>

        <div className="flex-1 overflow-y-auto py-4">
          {/* Domains Section */}
          <div className="px-3 mb-6">
            <div className="flex items-center justify-between mb-2 px-2">
              <Link
                to="/domains"
                className={`text-xs font-semibold uppercase tracking-wider ${
                  isActive("/domains") ? "text-white" : "text-slate-500"
                }`}
              >
                Domains
              </Link>
              <button
                onClick={() => setDialogOpen(true)}
                className="text-slate-500 hover:text-white transition-colors"
                title="Add Domain"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>

            {isLoading && (
              <div className="px-2 text-xs text-slate-600">Loading...</div>
            )}

            <div className="space-y-1">
              {domains.map((domain) => {
                const isExpanded = expandedDomains.has(domain.id);
                const isDomainActive = isActive(`/domains/${domain.id}`);

                return (
                  <div key={domain.id}>
                    <button
                      onClick={() => toggleDomain(domain.id)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                        isDomainActive
                          ? "bg-slate-800 text-white"
                          : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                      }`}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                      )}
                      <FolderIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{domain.name}</span>
                    </button>

                    {isExpanded && (
                      <div className="ml-6 mt-1 space-y-0.5">
                        <Link
                          to={`/domains/${domain.id}/documents`}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                            pathname === `/domains/${domain.id}/documents`
                              ? "bg-slate-800 text-white"
                              : "text-slate-500 hover:bg-slate-800 hover:text-slate-300"
                          }`}
                        >
                          <DocumentIcon className="h-3.5 w-3.5 shrink-0" />
                          Documents
                        </Link>
                        <Link
                          to={`/domains/${domain.id}/chat`}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                            pathname === `/domains/${domain.id}/chat`
                              ? "bg-slate-800 text-white"
                              : "text-slate-500 hover:bg-slate-800 hover:text-slate-300"
                          }`}
                        >
                          <ChatIcon className="h-3.5 w-3.5 shrink-0" />
                          Chat
                        </Link>
                        <Link
                          to={`/domains/${domain.id}/explore`}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                            pathname === `/domains/${domain.id}/explore`
                              ? "bg-slate-800 text-white"
                              : "text-slate-500 hover:bg-slate-800 hover:text-slate-300"
                          }`}
                        >
                          <NetworkIcon className="h-3.5 w-3.5 shrink-0" />
                          Explore
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Admin Section */}
          <div className="px-3 mb-6">
            <div className="px-2 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                User Admin
              </span>
            </div>
            <div className="space-y-1">
              <Link
                to="/admin/users"
                className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                  isActive("/admin/users")
                    ? "bg-slate-800 text-white"
                    : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                }`}
              >
                <UsersIcon className="h-4 w-4 shrink-0" />
                Users
              </Link>
              <Link
                to="/admin/rbac"
                className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                  isActive("/admin/rbac")
                    ? "bg-slate-800 text-white"
                    : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                }`}
              >
                <ShieldIcon className="h-4 w-4 shrink-0" />
                Roles & Permissions
              </Link>
            </div>
          </div>
        </div>

        {/* User */}
        <div className="border-t border-slate-800 p-3">
          <DropdownMenu
            align="start"
            placement="top"
            trigger={
              <button className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-slate-800 transition-colors">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-emerald-600 text-white text-xs">
                    {user.name?.[0]?.toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left overflow-hidden">
                  <div className="font-medium text-white truncate">{user.name}</div>
                  <div className="text-xs text-slate-500 truncate">{user.email}</div>
                </div>
              </button>
            }
          >
            <DropdownMenuItem onClick={handleLogout}>Sign out</DropdownMenuItem>
          </DropdownMenu>
        </div>
      </aside>

      {/* Add Domain Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogHeader>
          <DialogTitle>Create New Domain</DialogTitle>
          <DialogDescription>Create a new collection for organizing documents.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleCreateDomain} className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-white">Name *</label>
            <Input
              value={newDomainName}
              onChange={(e) => setNewDomainName(e.target.value)}
              placeholder="e.g. City Ordinances"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-white">Slug</label>
            <Input
              value={newDomainSlug}
              onChange={(e) => setNewDomainSlug(e.target.value)}
              placeholder="auto-generated from name"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-white">Description</label>
            <Input
              value={newDomainDesc}
              onChange={(e) => setNewDomainDesc(e.target.value)}
              placeholder="Optional description"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button type="submit" disabled={creating}>
              {creating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>
    </>
  );
}
